import { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';

import { CombinedStepType } from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateEditor/types';
import InquiryTemplatePreview from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplatePreview';
import LoadingIndicator from 'dashboard/components-lib/LoadingIndicator';
import { BlueprintBlockType, BlueprintPreviewMap } from 'dashboard/models/blueprint';
import { BuilderPreviewMap } from 'dashboard/models/inquiry-template';
import Button from 'design-system/components/Button';
import Spacer from 'design-system/components/Spacer';
import { Paragraph } from 'design-system/components/Text';
import { createMockCombinedStep } from 'inquiry/lib/stubs/inquiry';
import { StepPreview, UiStepType } from 'lib/interfaces/StepsConfig';

interface PreviewSolutionStepProps {
  blueprintKey: string;
  blueprintPreviews: BlueprintPreviewMap;
  inquiryTemplateBuilderPreviews: BuilderPreviewMap;
  isStepCompleted: boolean;
  isSavingCompletedStep: boolean;
  handleLoadBlueprintPreview: (blueprintKey: string) => Promise<void>;
  handleLoadInquiryTemplateBuilderPreview: (builderName: string) => Promise<void>;
  saveCompletedStep: () => void;
}

const PreviewSolutionStep = ({
  blueprintKey,
  blueprintPreviews,
  inquiryTemplateBuilderPreviews,
  isStepCompleted,
  isSavingCompletedStep,
  handleLoadBlueprintPreview,
  handleLoadInquiryTemplateBuilderPreview,
  saveCompletedStep,
}: PreviewSolutionStepProps) => {
  const [inquiryTemplateBuilderPreview, setInquiryTemplateBuilderPreview] = useState(null);
  const [isBlueprintPreviewLoading, setIsBlueprintPreviewLoading] = useState(true);

  useEffect(() => {
    if (blueprintPreviews[blueprintKey]) {
      const blueprintPreviewBlocks = blueprintPreviews[blueprintKey].blocks;
      // TODO: Handle when Blueprint has more than one inquiry template, once such a sandbox Blueprint exists
      // Also handle when Blueprint has zero inquiry templates (API only), once such a sandbox Blueprint exists
      const inquiryTemplate = blueprintPreviewBlocks.filter(
        (block) => block['block-type'] === BlueprintBlockType.InquiryTemplate,
      )[0];
      const inquiryTemplateBuilderName = inquiryTemplate['builder-name'];
      setIsBlueprintPreviewLoading(false);

      if (inquiryTemplateBuilderPreviews[inquiryTemplateBuilderName] == null) {
        handleLoadInquiryTemplateBuilderPreview(inquiryTemplateBuilderName);
      } else {
        setInquiryTemplateBuilderPreview(
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          inquiryTemplateBuilderPreviews[inquiryTemplateBuilderName],
        );
      }
    } else {
      handleLoadBlueprintPreview(blueprintKey);
    }
  }, [blueprintPreviews, inquiryTemplateBuilderPreviews]);

  const inquiryTemplateClientData = useMemo<StepPreview[]>(
    () =>
      (inquiryTemplateBuilderPreview ?? []).map((stepPreview: StepPreview | CombinedStepType) => {
        if (
          stepPreview === UiStepType.Document ||
          stepPreview === UiStepType.GovernmentId ||
          stepPreview === UiStepType.Selfie ||
          stepPreview === UiStepType.SelfieReflection
        ) {
          return createMockCombinedStep(stepPreview);
        } else {
          return stepPreview;
        }
      }),
    [inquiryTemplateBuilderPreview],
  );

  return (
    <>
      <Paragraph color='primary'>
        <b>Persona Inquiries</b> make it easy for you to collect sensitive identity information in a
        way that works seamlessly with your user journey. You can optimize the flow with our no-code{' '}
        <b>Flow Editor</b> and adjust friction based on the user’s risk.
      </Paragraph>
      <Spacer height={2} />
      <Paragraph color='primary'>
        In this step, you can see below what it’s like for a user to verify their identity
        information through an Inquiry.
      </Paragraph>
      <Spacer height={2} />
      {isBlueprintPreviewLoading ? (
        <LoadingIndicator />
      ) : (
        <InquiryTemplatePreviewContainer>
          <InquiryTemplatePreview clientData={inquiryTemplateClientData} scaleRatio={0.65} />
        </InquiryTemplatePreviewContainer>
      )}
      <Spacer height={2} />
      {!isStepCompleted && (
        <>
          {isSavingCompletedStep ? (
            <Button disabled width='button'>
              Saving...
            </Button>
          ) : (
            <Button
              id='home-sandbox-onboarding-checklist__preview-solution'
              width='button'
              onClick={() => {
                if (!isStepCompleted) {
                  saveCompletedStep();
                }
              }}
            >
              I’ve completed the preview!
            </Button>
          )}
        </>
      )}
    </>
  );
};

const InquiryTemplatePreviewContainer = styled.div`
  width: 90%;
  height: 500px;
`;

export default PreviewSolutionStep;
